import { useEffect, useState } from "react";
import { Calendar, X, Plus, Heart, Moon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function AnniversaryReminders() {
  const { user, refresh } = useAuth();
  const [dates, setDates] = useState([]);
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingQuiet, setSavingQuiet] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/loss-dates");
      setDates(data.dates || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!date) return;
    setBusy(true);
    try {
      await api.post("/loss-dates", { date, label: label || null });
      setDate(""); setLabel(""); setAdding(false);
      toast.success("We'll hold space with you.");
      load();
    } catch { toast.error("Could not save"); }
    setBusy(false);
  };

  const remove = async (id) => {
    try { await api.delete(`/loss-dates/${id}`); load(); }
    catch { toast.error("Could not remove"); }
  };

  const fmt = (iso) => new Date(iso + "T00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="bg-white border border-border rounded-2xl p-6 shadow-soft" data-testid="anniversary-reminders">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-sage-50 border border-sage-100 grid place-items-center shrink-0">
            <Heart size={18} weight="duotone" className="text-sage-700" />
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-sage-700">Anniversary reminders</div>
            <div className="font-display text-xl mt-1 leading-snug">Mark a date. We'll remember with you.</div>
            <p className="text-sm text-ink/70 mt-2 leading-relaxed max-w-lg">
              Add the date of a loss and Wingwomen will hold space quietly around the
              anniversary each year, a gentle note, no fanfare, entirely private.
            </p>
          </div>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid="add-loss-date"
                  className="shrink-0 flex items-center gap-1.5 text-xs bg-ink text-cream px-3 py-2 rounded-full hover:bg-coral transition-colors">
            <Plus size={12} weight="bold" /> Add date
          </button>
        )}
      </div>

      {adding && (
        <form onSubmit={add} className="mt-5 grid sm:grid-cols-[auto_1fr_auto] gap-2 items-center p-4 bg-sage-50 border border-sage-100 rounded-xl">
          <input
            type="date" required value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="loss-date-input"
            className="bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none"
          />
          <input
            type="text" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
            data-testid="loss-label-input"
            placeholder="Optional label (e.g. 'First trimester', a name)"
            className="bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-coral focus:ring-2 focus:ring-coral/20 outline-none"
          />
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => { setAdding(false); setDate(""); setLabel(""); }}
                    className="text-xs px-3 py-2 rounded-full text-muted-foreground hover:text-ink">
              Cancel
            </button>
            <button type="submit" disabled={busy || !date} data-testid="save-loss-date"
                    className="text-xs bg-coral text-white px-4 py-2 rounded-full hover:bg-coral-600 disabled:opacity-60 transition-colors">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      )}

      {dates.length > 0 && (
        <ul className="mt-5 space-y-2" data-testid="loss-dates-list">
          {dates.map((d) => (
            <li key={d.id} className="flex items-center gap-3 bg-cream/60 border border-border rounded-xl px-4 py-3">
              <Calendar size={16} weight="duotone" className="text-sage shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-display text-sm">{fmt(d.date)}</div>
                {d.label && <div className="text-xs text-muted-foreground truncate">{d.label}</div>}
              </div>
              <button onClick={() => remove(d.id)} data-testid={`remove-loss-${d.id}`}
                      aria-label="Remove date"
                      className="p-1.5 rounded-full text-muted-foreground hover:text-coral-700 hover:bg-coral-50 transition-colors">
                <X size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-5 text-[11px] text-muted-foreground leading-relaxed">
        Private to you. Never posted, never shared, never used for matching.
      </div>

      {/* Quiet Mode toggle */}
      <div className="mt-5 pt-5 border-t border-border flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-sage-50 border border-sage-100 grid place-items-center shrink-0">
          <Moon size={16} weight="duotone" className="text-sage-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base leading-tight">Quiet mode on anniversary days</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Hide matches and messages on the day of a loss so nothing feels loud.
          </div>
        </div>
        <button
          onClick={async () => {
            setSavingQuiet(true);
            try {
              await api.post("/settings", { quiet_on_anniversary: !user?.quiet_on_anniversary });
              await refresh();
              toast.success(!user?.quiet_on_anniversary ? "Quiet mode enabled." : "Quiet mode off.");
            } catch { toast.error("Could not save"); }
            setSavingQuiet(false);
          }}
          disabled={savingQuiet}
          data-testid="quiet-toggle"
          aria-pressed={user?.quiet_on_anniversary ? "true" : "false"}
          className={`relative w-12 h-7 rounded-full transition-colors ${
            user?.quiet_on_anniversary ? "bg-coral" : "bg-border"
          } disabled:opacity-60`}>
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            user?.quiet_on_anniversary ? "translate-x-5" : "translate-x-0"
          }`} />
        </button>
      </div>

      {/* Share with peers toggle */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-coral-50 border border-coral-100 grid place-items-center shrink-0">
          <Heart size={16} weight="duotone" className="text-coral-700" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base leading-tight">Let matches know when I'm nearing a tender date</div>
          <div className="text-xs text-muted-foreground mt-0.5">
            A small, private signal on match cards so a paired peer can send warmth.
          </div>
        </div>
        <button
          onClick={async () => {
            try {
              await api.post("/settings", { share_anniversary_with_peers: !user?.share_anniversary_with_peers });
              await refresh();
              toast.success(!user?.share_anniversary_with_peers ? "Sharing on." : "Sharing off.");
            } catch { toast.error("Could not save"); }
          }}
          data-testid="share-ann-toggle"
          aria-pressed={user?.share_anniversary_with_peers ? "true" : "false"}
          className={`relative w-12 h-7 rounded-full transition-colors ${
            user?.share_anniversary_with_peers ? "bg-coral" : "bg-border"
          }`}>
          <span className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-white shadow transition-transform ${
            user?.share_anniversary_with_peers ? "translate-x-5" : "translate-x-0"
          }`} />
        </button>
      </div>
    </div>
  );
}
